import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  'Free thank-you message templates for athletes — for a one-time gift, a new monthly supporter, and a season-end wrap-up. Copy, personalize, and send.';

const FAQS = [
  {
    q: 'Why does a thank-you message matter for fundraising?',
    a: "A specific thank-you is the difference between a one-time gift and a supporter who gives again next season. It also closes the loop — the person who gave wants to know it landed and mattered, not just that a payment went through.",
  },
  {
    q: 'How soon should I send a thank-you after a donation?',
    a: "Within a day or two, while it's still fresh. A quick, short message beats a longer one sent two weeks later. Sport Fund sends an automatic receipt at checkout, but a personal note from the athlete is what supporters actually remember.",
  },
  {
    q: 'Do I need to thank a monthly supporter every single month?',
    a: "No — a warm thank-you when they first set up recurring support, then a periodic update (not every charge) is enough. Monthly supporters want to see progress, not receive a repeat thank-you every 30 days.",
  },
  {
    q: 'Should I thank supporters publicly or privately?',
    a: "Both, if you can. A private message (text, email, or DM) feels personal and is where most of the relationship-building happens. A public shoutout — a story post, a team group chat mention — costs nothing extra and can prompt others in the group to give too.",
  },
  {
    q: 'What should I send at the end of the season?',
    a: 'A short wrap-up: what the season looked like, one specific thing their support made possible, and a photo if you have one. This is also the natural moment to mention next season, if you plan to keep fundraising.',
  },
];

const ThankYouMessageTemplates: React.FC = () => {
  usePageMeta(
    'Thank-You Message Templates for Sports Fundraising Supporters',
    ARTICLE_DESCRIPTION,
    {
      article: true,
      image: '/resources/athlete-celebration.jpg',
      breadcrumbs: [
        { name: 'Resources', path: '/resources' },
        { name: 'Thank-You Message Templates' },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    }
  );
  const TOC_SECTIONS = [
    {
      id: "1-for-a-one-time-gift",
      text: "1. For a one-time gift"
    },
    {
      id: "2-for-a-new-monthly-supporter",
      text: "2. For a new monthly supporter"
    },
    {
      id: "3-for-a-season-end-wrap-up",
      text: "3. For a season-end wrap-up"
    },
    {
      id: "4-for-a-local-business-sponsor",
      text: "4. For a local business sponsor"
    },
    {
      id: "what-actually-makes-a-thank-you-land",
      text: "What actually makes a thank-you land"
    },
    {
      id: "where-sport-fund-fits",
      text: "Where Sport Fund fits"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Thank-you message templates for sports fundraising supporters (2026)
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        The ask gets all the attention, but the thank-you is what turns a one-time gift into a supporter who's still
        there next season. Four templates you can copy today.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          Every fundraising guide talks about how to ask. Almost none talk about what to say after someone actually
          gives — which is a mistake, because a genuine thank-you is one of the cheapest, highest-return things an
          athlete can do. It costs a few minutes and it's the single biggest factor in whether a supporter gives
          again. Below are four templates for the moments that come up most: a one-time gift, a new monthly
          supporter, a season-end wrap-up, and a business sponsor.
        </p>

        <h2 id="1-for-a-one-time-gift">1. For a one-time gift</h2>
        <div className="not-prose bg-gray-50 border border-gray-200 rounded-lg p-6 my-6 whitespace-pre-line text-gray-800">
          {`Hi [Name],

Thank you so much for supporting me — your gift genuinely helps with [what it covers, e.g. "this month's travel to regionals"]. It means a lot to have you in my corner this season.

I'll keep you posted on how things go!

[Your name]`}
        </div>

        <h2 id="2-for-a-new-monthly-supporter">2. For a new monthly supporter</h2>
        <div className="not-prose bg-gray-50 border border-gray-200 rounded-lg p-6 my-6 whitespace-pre-line text-gray-800">
          {`Hi [Name],

Thank you for setting up monthly support — it makes a real difference knowing I have that steady help through the season, not just for one moment. I really appreciate you choosing to back me this way.

I'll share updates as the season goes, so you can see where it's going.

[Your name]`}
        </div>

        <h2 id="3-for-a-season-end-wrap-up">3. For a season-end wrap-up</h2>
        <div className="not-prose bg-gray-50 border border-gray-200 rounded-lg p-6 my-6 whitespace-pre-line text-gray-800">
          {`Hi [Name],

The season just wrapped up, and I wanted to say thank you one more time — your support helped cover [specific thing, e.g. "registration and travel to three tournaments"], and I couldn't have gotten through it without people like you.

[One specific highlight or photo, if you have one.]

Thank you for being part of this season. It meant a lot.

[Your name]`}
        </div>

        <h2 id="4-for-a-local-business-sponsor">4. For a local business sponsor</h2>
        <div className="not-prose bg-gray-50 border border-gray-200 rounded-lg p-6 my-6 whitespace-pre-line text-gray-800">
          {`Hi [Name],

Thank you for supporting me this season on behalf of [business name] — it's a huge help, and I want you to know it's appreciated. [Mention anything you offered in exchange, e.g. "I've tagged you in my season update post."]

I'll keep you posted on how the season goes, and thank you again.

[Your name]`}
        </div>

        <h2 id="what-actually-makes-a-thank-you-land">What actually makes a thank-you land</h2>
        <ul>
          <li>
            <strong>Be specific.</strong> "Thanks for the support" is fine; "thanks for helping cover this month's
            travel to regionals" is memorable. Name the real thing their gift touched.
          </li>
          <li>
            <strong>Send it soon.</strong> A day or two after the gift, while it's still top of mind for them.
          </li>
          <li>
            <strong>Keep it short.</strong> Two or three sentences is enough — supporters aren't expecting an essay,
            just to know it landed and mattered.
          </li>
          <li>
            <strong>Follow up later, not just once.</strong> A mid-season update and an end-of-season note keep a
            supporter connected to the actual journey, not just the transaction.
          </li>
        </ul>

        <h2 id="where-sport-fund-fits">Where Sport Fund fits</h2>
        <p>
          Sport Fund sends every supporter an automatic receipt the moment they give — but that's not the same as a
          thank-you from the athlete. Your fund page keeps every supporter's contact details in one place, so
          following up with the right template above takes minutes, not a search through old texts and emails.
        </p>
        <p>
          <Link to="/signup-creator" className="text-blue-600">
            Start a free fund page
          </Link>{' '}
          to keep your supporters organized from the first gift to the last thank-you.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/fundraising-letter-template" className="text-blue-600">
            Sports Fundraising Letter Template: A Free Letter You Can Copy
          </Link>{' '}
          — the ask that starts the relationship this thank-you continues.
        </p>
        <p>
          <Link to="/resources/engaging-your-supporters" className="text-blue-600">
            Engaging Your Supporters: Keep Family, Friends & Club in the Loop
          </Link>{' '}
          — more ways to keep supporters connected across a season.
        </p>
        <ArticleFeedback articleSlug="thank-you-message-templates" />
      </article>
    </div>
  );
};

export default ThankYouMessageTemplates;
